import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';

export const Settings: React.FC = () => {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const { user, setAuth } = useAuthStore();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  // Profile form
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    language: 'en',
    timezone: 'UTC',
  });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Privacy request form
  const [privacyRequest, setPrivacyRequest] = useState({
    type: 'access',
    description: '',
  });
  const [marketingConsent, setMarketingConsent] = useState(user?.marketingConsent ?? false);

  // Two-factor authentication state
  const [twoFactorSetup, setTwoFactorSetup] = useState<{ secret: string; qrCodeUrl: string } | null>(null);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [showTwoFactorDisable, setShowTwoFactorDisable] = useState(false);

  const isOwner = user?.role === 'owner';
  const canManage2FA = user?.role === 'admin' || user?.role === 'superadmin';
  const twoFactorEnabled = user?.twoFactorEnabled ?? false;

  // Update the auth store user while keeping tenant and tokens
  const updateUser = (updates: Record<string, any>) => {
    if (!user) return;
    setAuth({
      user: { ...user, ...updates },
      tenant: useAuthStore.getState().tenant!,
      accessToken: useAuthStore.getState().accessToken!,
      refreshToken: useAuthStore.getState().refreshToken!,
    });
  };

  // Fetch user profile
  const { data: profileData, isLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const response = await api.get('/auth/me');
      return response.data.data;
    },
  });

  // Update profile form when data loads
  useEffect(() => {
    if (profileData?.user) {
      setProfileForm({
        name: profileData.user.name || '',
        email: profileData.user.email || '',
        language: profileData.user.language || 'en',
        timezone: profileData.user.timezone || 'UTC',
      });
    }
  }, [profileData]);

  // Sync 2FA status and marketing consent from /auth/me into the store
  useEffect(() => {
    if (
      profileData?.user?.twoFactorEnabled !== undefined &&
      profileData.user.twoFactorEnabled !== user?.twoFactorEnabled
    ) {
      updateUser({ twoFactorEnabled: profileData.user.twoFactorEnabled });
    }
    if (profileData?.user?.marketingConsent !== undefined) {
      setMarketingConsent(profileData.user.marketingConsent);
    }
  }, [profileData]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.put('/auth/me', data);
      return response.data;
    },
    onSuccess: (data) => {
      // Update auth store with new user data
      if (user) {
        setAuth({
          user: { ...user, name: data.data.name, email: data.data.email },
          tenant: useAuthStore.getState().tenant!,
          accessToken: useAuthStore.getState().accessToken!,
          refreshToken: useAuthStore.getState().refreshToken!,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      showMessage(t('settings.profileSuccess'), 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('settings.profileError'), 'error');
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/auth/change-password', data);
      return response.data;
    },
    onSuccess: () => {
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showMessage(t('settings.passwordSuccess'), 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('settings.passwordError'), 'error');
    },
  });

  // Privacy request mutation
  const privacyRequestMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/tickets', data);
      return response.data;
    },
    onSuccess: () => {
      setPrivacyRequest({ type: 'access', description: '' });
      showMessage(t('settings.privacyRequestSuccess'), 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('settings.privacyRequestError'), 'error');
    },
  });

  // Marketing consent mutation
  const marketingConsentMutation = useMutation({
    mutationFn: async (consent: boolean) => {
      const response = await api.put('/auth/me/marketing-consent', { marketingConsent: consent });
      return response.data;
    },
    onSuccess: (data) => {
      setMarketingConsent(data.data.marketingConsent);
      showMessage(t('settings.marketingConsentSuccess'), 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('settings.marketingConsentError'), 'error');
    },
  });

  // 2FA setup mutation — fetches secret + QR code
  const setup2FAMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/auth/2fa/setup');
      return response.data.data;
    },
    onSuccess: (data) => {
      setTwoFactorSetup({ secret: data.secret, qrCodeUrl: data.qrCodeUrl });
      setTwoFactorToken('');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('settings.twoFactorError'), 'error');
    },
  });

  // 2FA verify mutation — activates 2FA with a TOTP token
  const verify2FAMutation = useMutation({
    mutationFn: async (token: string) => {
      const response = await api.post('/auth/2fa/verify', { token });
      return response.data;
    },
    onSuccess: () => {
      updateUser({ twoFactorEnabled: true });
      setTwoFactorSetup(null);
      setTwoFactorToken('');
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      showMessage(t('settings.twoFactorEnabledSuccess'), 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('settings.twoFactorVerifyError'), 'error');
    },
  });

  // 2FA disable mutation
  const disable2FAMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete('/auth/2fa');
      return response.data;
    },
    onSuccess: () => {
      updateUser({ twoFactorEnabled: false });
      setShowTwoFactorDisable(false);
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      showMessage(t('settings.twoFactorDisabledSuccess'), 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('settings.twoFactorError'), 'error');
    },
  });

  const showMessage = (msg: string, type: 'success' | 'error') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updates: any = {};
    if (profileForm.name !== profileData?.user?.name) updates.name = profileForm.name;
    if (profileForm.email !== profileData?.user?.email) updates.email = profileForm.email;
    if (profileForm.language !== profileData?.user?.language) updates.language = profileForm.language;
    if (profileForm.timezone !== profileData?.user?.timezone) updates.timezone = profileForm.timezone;

    if (Object.keys(updates).length === 0) {
      showMessage(t('settings.noChanges'), 'error');
      return;
    }

    updateProfileMutation.mutate(updates);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showMessage(t('settings.passwordsNoMatch'), 'error');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      showMessage(t('settings.passwordMinLength'), 'error');
      return;
    }
    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await api.delete('/auth/me');
      setShowDeleteModal(false);
      showMessage(t('settings.deleteRequested'), 'success');
    } catch (error: any) {
      showMessage(error.response?.data?.error || t('settings.deleteError'), 'error');
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTwoFactorVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(twoFactorToken)) {
      showMessage(t('settings.twoFactorTokenInvalid'), 'error');
      return;
    }
    verify2FAMutation.mutate(twoFactorToken);
  };

  const handlePrivacyRequestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!privacyRequest.description.trim()) {
      showMessage(t('settings.privacyRequestDescriptionRequired'), 'error');
      return;
    }

    const typeLabels: Record<string, string> = {
      access: t('settings.privacyTypeAccess'),
      rectification: t('settings.privacyTypeRectification'),
      erasure: t('settings.privacyTypeErasure'),
      portability: t('settings.privacyTypePortability'),
      objection: t('settings.privacyTypeObjection'),
      restriction: t('settings.privacyTypeRestriction'),
    };

    privacyRequestMutation.mutate({
      subject: `[Privacy Request] ${typeLabels[privacyRequest.type] || privacyRequest.type}`,
      description: privacyRequest.description.trim(),
      category: 'privacy_request',
      priority: 'high',
    });
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value;
    setProfileForm({ ...profileForm, language: newLang });
    i18n.changeLanguage(newLang);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">{t('settings.loading')}</div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">{t('settings.title')}</h1>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          messageType === 'success' 
            ? 'bg-green-500/10 border border-green-500/20 text-green-400' 
            : 'bg-red-500/10 border border-red-500/20 text-red-400'
        }`}>
          {message}
        </div>
      )}

      <div className="space-y-8">
        {/* Account Information */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-6">{t('settings.accountInfo')}</h2>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.name')}</label>
                <input
                  type="text"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.email')}</label>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.language')}</label>
                <select
                  value={profileForm.language}
                  onChange={handleLanguageChange}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                >
                  <option value="en">{t('settings.languageOptions.en')}</option>
                  <option value="es">{t('settings.languageOptions.es')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.timezone')}</label>
                <select
                  value={profileForm.timezone}
                  onChange={(e) => setProfileForm({ ...profileForm, timezone: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                >
                  <option value="UTC">{t('settings.timezoneOptions.UTC')}</option>
                  <option value="America/New_York">{t('settings.timezoneOptions.ET')}</option>
                  <option value="America/Chicago">{t('settings.timezoneOptions.CT')}</option>
                  <option value="America/Denver">{t('settings.timezoneOptions.MT')}</option>
                  <option value="America/Los_Angeles">{t('settings.timezoneOptions.PT')}</option>
                  <option value="Europe/London">{t('settings.timezoneOptions.GMT')}</option>
                  <option value="Europe/Paris">{t('settings.timezoneOptions.CET')}</option>
                  <option value="Europe/Madrid">{t('settings.timezoneOptions.CET')}</option>
                  <option value="Asia/Tokyo">{t('settings.timezoneOptions.JST')}</option>
                  <option value="Asia/Singapore">{t('settings.timezoneOptions.SGT')}</option>
                  <option value="Australia/Sydney">{t('settings.timezoneOptions.AEDT')}</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={updateProfileMutation.isPending}
                className="px-6 py-2 bg-[#006cd4] hover:bg-[#0052a3] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {updateProfileMutation.isPending ? t('settings.savingChanges') : t('settings.saveChanges')}
              </button>
            </div>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-6">{t('settings.changePassword')}</h2>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.currentPassword')}</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                  required
                />
              </div>
              <div className="hidden md:block" />
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.newPassword')}</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.confirmNewPassword')}</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                  required
                  minLength={8}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={changePasswordMutation.isPending}
                className="px-6 py-2 bg-[#006cd4] hover:bg-[#0052a3] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {changePasswordMutation.isPending ? t('settings.updatingPassword') : t('settings.updatePassword')}
              </button>
            </div>
          </form>
        </div>

        {/* Two-Factor Authentication */}
        {canManage2FA && (
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-6">{t('settings.twoFactorTitle')}</h2>
            {twoFactorEnabled ? (
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">{t('settings.twoFactorEnabled')}</h3>
                  <p className="text-sm text-gray-400">{t('settings.twoFactorEnabledDesc')}</p>
                </div>
                <button
                  onClick={() => setShowTwoFactorDisable(true)}
                  className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg transition-colors"
                >
                  {t('settings.twoFactorDisable')}
                </button>
              </div>
            ) : twoFactorSetup ? (
              <form onSubmit={handleTwoFactorVerify} className="space-y-4">
                <p className="text-sm text-gray-400">{t('settings.twoFactorScanDesc')}</p>
                <div className="flex flex-col items-center gap-4">
                  <img
                    src={twoFactorSetup.qrCodeUrl}
                    alt={t('settings.twoFactorQrAlt')}
                    className="w-48 h-48 rounded-lg bg-white p-2"
                  />
                  <div className="text-center">
                    <p className="text-sm text-gray-400 mb-2">{t('settings.twoFactorManualEntry')}</p>
                    <code className="px-3 py-1 bg-[#1a1a25] border border-white/10 rounded text-sm text-[#00d4aa] break-all">
                      {twoFactorSetup.secret}
                    </code>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.twoFactorTokenLabel')}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={twoFactorToken}
                    onChange={(e) => setTwoFactorToken(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4] tracking-widest text-center"
                    placeholder="000000"
                    required
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => { setTwoFactorSetup(null); setTwoFactorToken(''); }}
                    className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={verify2FAMutation.isPending}
                    className="px-6 py-2 bg-[#006cd4] hover:bg-[#0052a3] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {verify2FAMutation.isPending ? t('settings.twoFactorVerifying') : t('settings.twoFactorVerifyButton')}
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">{t('settings.twoFactorDisabled')}</h3>
                  <p className="text-sm text-gray-400">{t('settings.twoFactorDisabledDesc')}</p>
                </div>
                <button
                  onClick={() => setup2FAMutation.mutate()}
                  disabled={setup2FAMutation.isPending}
                  className="px-4 py-2 bg-[#006cd4] hover:bg-[#0052a3] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {setup2FAMutation.isPending ? t('settings.twoFactorLoading') : t('settings.twoFactorEnable')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Privacy & Data */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-6">{t('settings.privacyTitle')}</h2>

          {/* Marketing consent */}
          <div className="flex items-start gap-3 mb-6 pb-6 border-b border-white/10">
            <input
              type="checkbox"
              id="marketingConsentSetting"
              checked={marketingConsent}
              onChange={(e) => marketingConsentMutation.mutate(e.target.checked)}
              disabled={marketingConsentMutation.isPending}
              className="mt-1 w-4 h-4 rounded border-white/10 bg-[#1a1a25] text-[#006cd4] focus:ring-[#006cd4]"
            />
            <div>
              <label htmlFor="marketingConsentSetting" className="block text-sm font-medium text-gray-300 cursor-pointer">
                {t('settings.marketingConsentLabel')}
              </label>
              <p className="text-sm text-gray-500 mt-1">{t('settings.marketingConsentDesc')}</p>
            </div>
          </div>

          {/* Privacy request form */}
          <form onSubmit={handlePrivacyRequestSubmit} className="space-y-4 mt-6">
            <p className="text-sm text-gray-400">{t('settings.privacyRequestDesc')}</p>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.privacyRequestType')}</label>
              <select
                value={privacyRequest.type}
                onChange={(e) => setPrivacyRequest({ ...privacyRequest, type: e.target.value })}
                className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
              >
                <option value="access">{t('settings.privacyTypeAccess')}</option>
                <option value="rectification">{t('settings.privacyTypeRectification')}</option>
                <option value="erasure">{t('settings.privacyTypeErasure')}</option>
                <option value="portability">{t('settings.privacyTypePortability')}</option>
                <option value="objection">{t('settings.privacyTypeObjection')}</option>
                <option value="restriction">{t('settings.privacyTypeRestriction')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.privacyRequestDescription')}</label>
              <textarea
                value={privacyRequest.description}
                onChange={(e) => setPrivacyRequest({ ...privacyRequest, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                placeholder={t('settings.privacyRequestDescriptionPlaceholder')}
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={privacyRequestMutation.isPending}
                className="px-6 py-2 bg-[#006cd4] hover:bg-[#0052a3] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {privacyRequestMutation.isPending ? t('settings.privacyRequestSending') : t('settings.privacyRequestSend')}
              </button>
            </div>
          </form>

          <p className="text-xs text-gray-500 mt-4">
            {t('settings.privacyRequestNote')}{' '}
            <a href="mailto:privacy@r3converse.com" className="text-[#006cd4] hover:underline">
              privacy@r3converse.com
            </a>
          </p>
        </div>

        {/* Danger Zone */}
        <div className="bg-[#12121a] border border-red-500/20 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-red-400 mb-4">{t('settings.dangerZone')}</h2>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">{t('settings.deleteAccount')}</h3>
              <p className="text-sm text-gray-400">{t('settings.deleteAccountDesc')}</p>
            </div>
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={!isOwner}
              title={!isOwner ? t('settings.deleteOwnerOnly') : undefined}
              className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('settings.deleteAccount')}
            </button>
          </div>
          {!isOwner && (
            <p className="text-xs text-gray-500 mt-3">{t('settings.deleteOwnerOnly')}</p>
          )}
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-red-400 mb-4">{t('settings.deleteModalTitle')}</h3>
            <p className="text-gray-300 mb-6">
              {t('settings.deleteModalDesc')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isDeleting ? t('settings.deletingAccount') : t('settings.deleteModalButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable 2FA Modal */}
      {showTwoFactorDisable && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-red-400 mb-4">{t('settings.twoFactorDisableTitle')}</h3>
            <p className="text-gray-300 mb-6">
              {t('settings.twoFactorDisableDesc')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowTwoFactorDisable(false)}
                className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => disable2FAMutation.mutate()}
                disabled={disable2FAMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {disable2FAMutation.isPending ? t('settings.twoFactorDisabling') : t('settings.twoFactorDisable')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
